import { MutableState } from '../State';
import { sendAnalyticsEvents } from '../analytics';
import {
  CurrencyGrantFeature,
  CurrencyConsumeFeature,
  getCurrencyConsumeEvent,
  getCurrencyGrantEvent,
  AdditionalCurrencyGrantProperties,
  AdditionalCurrencyConsumeProperties,
} from '../analytics/events';
import * as Inventory from '../inventory';
import { SyncActionAPI } from '../utils/types';

/**
 * Replicant-like interface, but with only the methods needed for currency
 */
export type CurrencyReplicantAPI = SyncActionAPI;

/**
 * Currency map as returned by inventory.getCurrencies function
 */
export type Currencies = {
  points: number;
  energy: number;
  freeGems: number;
  paidGems: number;
  stars: number;
};

/**
 * Check if the currency has changed between two sets of currencies.
 * Returns true if it has, false otherwise.
 */
function checkCurrencyChanged(
  oldCurrencies: Record<string, number>,
  newCurrencies: Record<string, number>,
) {
  for (const key in oldCurrencies) {
    if (oldCurrencies[key] !== newCurrencies[key]) return true;
  }

  return false;
}

/**
 * Optional arguments for currency grant.
 */
export type CurrencyGrantOpts = {
  excludeEnergy?: boolean;
  getAdditionalProperties?: (
    oldCurrencies: Currencies,
    newCurrencies: Currencies,
  ) => AdditionalCurrencyGrantProperties;
  userProperties?: Record<string, unknown>;
  purchaseID?: string;
};

/**
 * Dispatch a method that grants currency to the player,
 * and send a CurrencyGrant analytics event if the currency has changed.
 */
export function grantCurrency(
  state: MutableState,
  modifiers: () => void,
  feature: CurrencyGrantFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyGrantOpts,
) {
  const now = api.date.now();
  const oldCurrencies = Inventory.getters.getCurrencies(state, now);

  modifiers();

  const newCurrencies = Inventory.getters.getCurrencies(state, now);
  const hasChanged = checkCurrencyChanged(oldCurrencies, newCurrencies);
  if (!hasChanged) return;

  const analyticsEvent = getCurrencyGrantEvent(
    feature,
    {
      old: oldCurrencies,
      new: newCurrencies,
    },
    now,
    opts?.getAdditionalProperties?.(oldCurrencies, newCurrencies),
    opts?.excludeEnergy,
    opts?.purchaseID,
  );

  sendAnalyticsEvents(api, [
    {
      ...analyticsEvent,
      userProperties: opts?.userProperties,
    },
  ]);
}

/**
 * Optional arguments for currency consume.
 */
export type CurrencyConsumeOpts = {
  excludeEnergy?: boolean;
  getAdditionalProperties?: (
    oldCurrencies: Currencies,
    newCurrencies: Currencies,
  ) => AdditionalCurrencyConsumeProperties;
  userProperties?: Record<string, unknown>;
};

/**
 * Dispatch a method that grants currency to the player,
 * and send a CurrencyConsume analytics event if the currency has changed.
 */
export function consumeCurrency(
  state: MutableState,
  modifiers: () => void,
  feature: CurrencyConsumeFeature,
  api: CurrencyReplicantAPI,
  opts?: CurrencyConsumeOpts,
) {
  const now = api.date.now();
  const oldCurrencies = Inventory.getters.getCurrencies(state, now);

  modifiers();

  const newCurrencies = Inventory.getters.getCurrencies(state, now);
  const hasChanged = checkCurrencyChanged(oldCurrencies, newCurrencies);
  if (!hasChanged) return;

  const analyticsEvent = getCurrencyConsumeEvent(
    feature,
    {
      old: oldCurrencies,
      new: newCurrencies,
    },
    now,
    opts?.getAdditionalProperties?.(oldCurrencies, newCurrencies),
    opts?.excludeEnergy,
  );

  sendAnalyticsEvents(api, [
    {
      ...analyticsEvent,
      userProperties: opts?.userProperties,
    },
  ]);
}

/**
 * Retrieve a dummy API, for cases where api is not available (e.g. tests, )
 * This is because the api is a required argument by currency add & consume modifiers, but some
 * places in the project (like messages and unit tests) don't have access to the api, so this can be used instead.
 * Analytics events will not be sent when using this API.
 */
export function getDummyAPI(): CurrencyReplicantAPI {
  return {
    date: {
      now: () => 0,
    },
    sendAnalyticsEvents: () => {},
    scheduledActions: {
      schedule: {
        expireTimedReward: () => {},
      },
    },
  } as unknown as CurrencyReplicantAPI;
}
